import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	OrderMessagesService,
	type IGetOrderMessagesParams,
} from '@services/order-messages.service';

// --- Stores ---
import { OrderMessagesStore } from '@stores/order-messages.store';

// --- Interfaces ---
import type {
	IAttachment,
	IOrderMessage,
	IUnreadGrouped,
} from '@interfaces/orderMessage';
import type { AttachFileData } from '@interfaces/file';

const REFRESH_INTERVAL = 6 * 10000;

@Injectable({
	providedIn: 'root',
})
export class OrderMessagesFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);

	private readonly _filter = new BehaviorSubject<IGetOrderMessagesParams>({});
	readonly filter$ = this._filter.asObservable().pipe(distinctUntilChanged());

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getOrderMessages({ ...this._filter.value })));

	readonly orderMessagesState$ = combineLatest([
		this.orderMessagesStore.orderMessagesState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private orderMessagesService: OrderMessagesService,
		private orderMessagesStore: OrderMessagesStore
	) {}

	getOrderMessages(queryParams?: IGetOrderMessagesParams) {
		return this.orderMessagesService
			.getOrderMessages(queryParams)
			.pipe(
				tap(orderMessages =>
					this.orderMessagesStore.updateOrderMessages(orderMessages)
				)
			);
	}

	getUnreadOrderMessages() {
		return this.orderMessagesService
			.getOrderMessages({ visualized: false })
			.pipe();
	}

	getOrderMessageById(id: IOrderMessage['id']) {
		return this.orderMessagesService.getOrderMessage(id);
	}

	newOrderMessage(orderMessage: IOrderMessage) {
		return this.orderMessagesService
			.newOrderMessage(orderMessage)
			.pipe(
				tap(orderMessage =>
					this.orderMessagesStore.updateOrderMessage(orderMessage)
				)
			);
	}

	visualized(messageIds: number[]) {
		return this.orderMessagesService.visualized(messageIds).pipe(tap());
	}

	deleteOrderMessage(orderMessage: IOrderMessage) {
		return this.orderMessagesService
			.deleteOrderMessage(orderMessage.id)
			.pipe(
				tap(() => this.orderMessagesStore.deleteOrderMessage(orderMessage))
			);
	}

	filterOrderMessages(filter: IGetOrderMessagesParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}

	clearFilters() {
		this._filter.next({});
	}

	postAttachment(file: AttachFileData, data: any) {
		return this.orderMessagesService.newAttachment(file, data);
	}

	deleteAttachment(attachmentId: IAttachment['id']) {
		return this.orderMessagesService.deleteAttachment(attachmentId);
	}
}
